import { IntlKey } from 'locales';
import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { Overlay, Popover, PopoverBody } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';

type PendingLink = {
  language: string | null;
  status: 'pending';
};

type ErrorLink = {
  language: string | null;
  status: 'error';
  severity: 'warning' | 'danger';
  onShowError?: Nullable<() => void>;
};

type SuccessLink = {
  language: string | null;
  status: 'success';
  onDownload: () => void;
};

function SuccessLink({ language, onDownload }: SuccessLink) {
  return (
    <a className="btn btn-link btn-sm btn-icon-end" onClick={onDownload}>
      {language ?? ''}
      <i className="icon fs-3">save_alt</i>
    </a>
  );
}

const variants = {
  warning: {
    iconName: 'error_outline',
    title: 'Document can not be generated due to error.',
  },
  danger: {
    iconName: 'block',
    title: 'This document has not yet been validated.',
  },
};

function ErrorLink({ language, severity, onShowError, children }: PropsWithChildren<ErrorLink>) {
  const [infoIcon, setInfoIcon] = useState<HTMLElement | null>(null);
  const [isOpened, setOpened] = useState<boolean>(false);
  const toggleOpened = useCallback(() => setOpened(v => !v), []);
  const config = variants[severity];
  const modalRoot = document.getElementById('modal-root') ?? document.body;

  useEffect(() => {
    if (infoIcon !== null) {
      setOpened(true);
    }
  }, [infoIcon]);

  return (
    <>
      <span
        className="btn btn-link btn-sm btn-icon-end"
        data-bs-container="body"
        data-bs-custom-class={`tooltip-${severity}`}
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        data-bs-title={config.title}
        onClick={onShowError ?? toggleOpened}
      >
        {language ?? ''}
        <i ref={setInfoIcon} className={`icon text-${severity}`}>
          {config.iconName}
        </i>
      </span>

      {createPortal(
        <Overlay target={infoIcon} show={isOpened} placement="top" rootClose={false}>
          <Popover>
            <PopoverBody as="div" className="bg-lvl2 text-primary p-2">
              {children}
            </PopoverBody>
          </Popover>
        </Overlay>,
        modalRoot,
      )}
    </>
  );
}

function PendingLink({ language }: PendingLink) {
  return (
    <a className="btn btn-link btn-sm btn-icon-end loading">
      {language ?? ''}
      <span className="loader"></span>
      <span className="icon">close</span>
    </a>
  );
}

type DistributiveOmit<T, K extends keyof any> = T extends unknown ? Omit<T, K> : never;

type Props = { langCode: string | null | undefined } & DistributiveOmit<
  PendingLink | ErrorLink | SuccessLink,
  'language'
>;

export function LanguageLink({ langCode, ...props }: PropsWithChildren<Props>) {
  const { formatMessage } = useIntl();
  const language =
    langCode !== null && langCode !== undefined
      ? formatMessage({
          id: `languageCode.${langCode}` as IntlKey,
          defaultMessage: langCode,
        })
      : null;

  switch (props.status) {
    case 'success':
      return <SuccessLink language={language} {...props} />;
    case 'error':
      return <ErrorLink language={language} {...props} />;
    case 'pending':
      return <PendingLink language={language} {...props} />;
    default:
      return null;
  }
}
